import { ref } from '@vue/composition-api'
import useHttpPost from 'states/generics/useHttpPost'
import { debounce } from 'lodash'

const BATCH_TIME_WINDOW = 50 // time since first meta-info fetch until we need to perform batch request. In ms

const globalMetaInfoFetchState = {
  fetch_queue: [],
  results: {},
  fetchPromise: null,
  resolveFetchPromise: null,
}

window.globalMetaInfoFetchState = globalMetaInfoFetchState

const fetchAllPending = async () => {
  const pendingItems = [...globalMetaInfoFetchState.fetch_queue]
  globalMetaInfoFetchState.fetch_queue = []

  const getOrgsMap = () => {
    const orgsMap = {}
    pendingItems.forEach((item) => {
      const { orgId, startupId } = item
      if (!orgsMap[orgId]) {
        orgsMap[orgId] = []
      }
      orgsMap[orgId].push(startupId)
    })
    return orgsMap
  }
  const orgsMap = getOrgsMap()

  const postDataList = []
  const promises = []
  const responses = {}
  Object.keys(orgsMap).forEach((orgId) => {
    const url = `/organizations/${orgId}/startups/batch-meta-info`
    const data = { startup_ids: orgsMap[orgId] }
    const { postInProgress, post, response } = useHttpPost({ url })
    promises.push(post(data))
    postDataList.push({ orgId, response, postInProgress })
  })

  await Promise.all(promises)
  postDataList.forEach(({ orgId, response }) => {
    console.log(' => response.data:', response.value)
    const data = response.value.data
    Object.keys(data.startups).forEach((startupId) => {
      const key = getFetchKey({ orgId, startupId })
      globalMetaInfoFetchState.results[key] = data.startups[startupId]
    })
  })

  if (globalMetaInfoFetchState.resolveFetchPromise) {
    globalMetaInfoFetchState.resolveFetchPromise()
    globalMetaInfoFetchState.fetchPromise = null
    globalMetaInfoFetchState.resolveFetchPromise = null
  }
}

const getFetchKey = ({ orgId, startupId }) => {
  return `${orgId}:${startupId}`
}

const debouncedFetchAllPending = debounce(fetchAllPending, BATCH_TIME_WINDOW)

const fetch = async ({ orgId, startupId }) => {
  globalMetaInfoFetchState.fetch_queue.push({ orgId, startupId })
  if (!globalMetaInfoFetchState.fetchPromise) {
    globalMetaInfoFetchState.fetchPromise = new Promise((r) => {
      globalMetaInfoFetchState.resolveFetchPromise = r
    })
  }
  debouncedFetchAllPending()
  return globalMetaInfoFetchState.fetchPromise
}

export default function useConnectWithStartup({
  orgId,
  startupId,
  is_obfuscated,
}) {
  const url = `/organizations/${orgId}/startups/${startupId}/start-chat`
  const { postInProgress, post } = useHttpPost({ url, sleep: 2000 })

  const { isConnected, threadId, fetchConnection, fetchConnectionInProgress } =
    useFetchConnection({ orgId, startupId, is_obfuscated })

  const connect = async ({ message }) => {
    console.log(` => useConnectWithStartup
    orgId: ${orgId}
    startupId: ${startupId}
    `)

    await post({ message })
    await fetchConnection()
  }

  return {
    connect,
    postInProgress,

    isConnected,
    threadId,
    fetchConnectionInProgress,
  }
}

function useFetchConnection({ orgId, startupId, is_obfuscated: isObfuscated }) {
  const isConnected = ref(false)
  const threadId = ref(null)
  const fetchConnectionInProgress = ref(false)

  const fetchConnection = async () => {
    fetchConnectionInProgress.value = true
    await fetch({ orgId, startupId })
    const key = getFetchKey({ orgId, startupId })
    const metaData = globalMetaInfoFetchState.results[key]
    if (metaData && metaData.thread_id) {
      threadId.value = metaData.thread_id
      isConnected.value = true
    }
    fetchConnectionInProgress.value = false
  }

  if (orgId && startupId && !isObfuscated) fetchConnection()

  return {
    isConnected,
    threadId,
    fetchConnection,
    fetchConnectionInProgress,
  }
}
